import { useAtom } from 'jotai'
import {
  allProductIdsAtom,
  productTimelinesAtom,
  timelineMinimumsAtom
} from './atoms/timeline.atom'
import { Box, Flex } from '@chakra-ui/react'
import {
  BILLING_PERIOD_PILL_OFFSET_FROM_DATE_PX,
  TIMELINE_PHASE_ROW_HEIGHT_PX,
  TIMELINE_PRODUCT_ROW_HEIGHT_PX
} from './timeline.constants'
import {
  GreyGrey60,
  GreyGrey80,
  Lato12Bold,
  Lato12Regular
} from '@sequencehq/design-tokens'
import { timelineLeftScrollPositionAtom } from './atoms/timelineUi.atom'
import { Fragment } from 'react'
import { FormattedPriceSummary } from './utils/FormattedPriceSummary'

const useScheduleTimelineLabels = () => {
  const [productIds] = useAtom(allProductIdsAtom)
  const [productTimelines] = useAtom(productTimelinesAtom)
  const [timelineLeftScroll] = useAtom(timelineLeftScrollPositionAtom)
  const [minimumTimeline] = useAtom(timelineMinimumsAtom)

  const productLabels = productIds.map(id => {
    const productTimeline = productTimelines[id]

    return {
      id,
      labels: productTimeline.prices.map((price, idx) => {
        const nextPrice = productTimeline.prices[idx + 1]

        const priceEndOffset = !nextPrice ? null : nextPrice.startDate.offset
        return {
          label: price.name,
          id: price.id,
          summary: price.summary,
          marginLeft:
            price.startDate.offset + BILLING_PERIOD_PILL_OFFSET_FROM_DATE_PX,
          show: !priceEndOffset || timelineLeftScroll + 150 < priceEndOffset,
          labelEndOffset: priceEndOffset,
          isLastLabel: idx === productTimeline.prices.length - 1,

          summaryEndOffset: !priceEndOffset
            ? productTimeline.billingPeriods.at(-1)?.endOffset
            : priceEndOffset
        }
      })
    }
  })

  const minimumsLabels = minimumTimeline.length
    ? {
        id: 'minimum',
        labels: minimumTimeline.map((minimum, idx) => {
          const nextMinimum = minimumTimeline[idx + 1]

          const minimumEndOffset = !nextMinimum
            ? null
            : nextMinimum.startDate.offset
          return {
            label: 'Minimum commitment',
            id: minimum.id,
            summary: `${minimum.summary.amount} / ${minimum.summary.frequency}`,
            marginLeft:
              minimum.startDate.offset +
              BILLING_PERIOD_PILL_OFFSET_FROM_DATE_PX,
            show:
              !minimumEndOffset || timelineLeftScroll + 150 < minimumEndOffset,
            labelEndOffset: minimumEndOffset,
            isLastLabel: idx === minimumTimeline.length - 1,
            summaryEndOffset: !minimumEndOffset
              ? minimumTimeline.at(-1)?.endDate.offset
              : minimumEndOffset
          }
        })
      }
    : null

  return {
    labels: minimumsLabels ? [minimumsLabels, ...productLabels] : productLabels
  }
}

export const ScheduleTimelineLabels = () => {
  const { labels } = useScheduleTimelineLabels()
  return (
    <Box>
      <Box height={`${TIMELINE_PHASE_ROW_HEIGHT_PX}px`}></Box>
      {labels.map(({ id, labels }) => {
        return (
          <Fragment key={`timeline.productLabels.${id}`}>
            {labels.map(label => {
              return (
                <>
                  <Flex
                    position="relative"
                    key={`timeline.productLabels.${id}.price.${label.id}`}
                    data-testid={`timeline.productLabels.${id}.price.${label.id}`}
                    height="0"
                    overflowY="visible"
                    overflowX="clip"
                    width={
                      label.labelEndOffset
                        ? `${label.labelEndOffset}px`
                        : '100%'
                    }
                  >
                    <Box
                      marginTop="8px"
                      position="sticky"
                      marginLeft={`${label.marginLeft}px`}
                      left={`48px`}
                      {...Lato12Bold}
                      color={GreyGrey80}
                      width="0"
                      whiteSpace="nowrap"
                      overflow="visible"
                      userSelect="none"
                      opacity={label.show ? 1 : 0}
                      transition={'opacity 0.15s ease-in-out'}
                    >
                      {label.label}
                    </Box>
                  </Flex>
                  <Flex
                    position="relative"
                    key={`timeline.productLabels.${id}.summary.${label.id}`}
                    data-testid={`timeline.productLabels.${id}.summary.${label.id}`}
                    height="0"
                    overflowY="visible"
                    overflowX="clip"
                    width={label.isLastLabel ? label.summaryEndOffset : '100%'}
                  >
                    <Box
                      marginTop="48px"
                      position="sticky"
                      marginLeft={`${label.marginLeft}px`}
                      left={`48px`}
                      {...Lato12Regular}
                      color={GreyGrey60}
                      width="0"
                      whiteSpace="nowrap"
                      overflow="visible"
                      userSelect="none"
                      opacity={label.show ? 1 : 0}
                      transition={'opacity 0.15s ease-in-out'}
                    >
                      {typeof label.summary === 'string' ? (
                        label.summary
                      ) : (
                        <FormattedPriceSummary {...label.summary} />
                      )}
                    </Box>
                  </Flex>
                </>
              )
            })}
            <Box height={`${TIMELINE_PRODUCT_ROW_HEIGHT_PX}px`}></Box>
          </Fragment>
        )
      })}
    </Box>
  )
}
